import React, { useState } from "react";
import { Space, Button, Row, Col, Typography, TabsProps, Tabs, notification, Tooltip } from "antd";

import type { ColumnsType } from "antd/es/table";
import {
  LoadingOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  CloudDownloadOutlined,
  ShareAltOutlined,
  SendOutlined,
  FilePdfOutlined,
  CloudUploadOutlined
} from "@ant-design/icons";
import {
  useSendCustomerReportMutation,
  useViewCustomerReportMutation
} from "../../../redux/services/assignmentApi";
import { useGetInstallationsQuery } from "../../../redux/services/installationAPI";
import { useBulkCustomerReportsMutation } from "../../../redux/services/reportApi";
import TopScrollTable from "../../../components/TopScrollTable";
import Filters from "../Filters";
import { Link, useNavigate } from "react-router-dom";
import {
  localizeDateTime,
  isUserHasRoleTypes,
  getStatusText,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { BASE_URL } from "../../../redux/services/baseApi";
import { useAppSelector } from "../../../redux/hooks";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

interface Installation {
  id: string;
  ecb_install_code: string;
}

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "All Installations"
  },
  {
    key: "2",
    label: "Payment Pending Installations"
  },
  {
    key: "3",
    label: "Payment Closed Installations"
  }
];

const { Text } = Typography;

const Installation = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    ecb_code: parseInt(querySearchParams.ecb_code),
    page: parseInt(querySearchParams.page) || 1,
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const auth = useAppSelector<any>((store) => store.auth);
  const [downloadInProgress, setDownloadInProgress] = useState<any>({});
  const [tabKey, setTabKey] = useState("1");
  const [api, contextHolder] = notification.useNotification();
  const [bulkReportLoading, setBulkReportLoading] = useState(false);
  const [bulkCustomerReports] = useBulkCustomerReportsMutation();

  const { data, isLoading, refetch, isFetching } = useGetInstallationsQuery({
    ...queryParams
  });
  const installations = data?.installations || [];

  const user = auth.user;
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );

  const isSuperUser = (user: any) => {
    return isUserHasRoleTypes(user, ["super_user"]);
  };

  const isEditorUser = user?.roles?.some(
    (role: { role_type: string }) =>
      ["mis", "super_user", "finance_controller"].includes(role.role_type) // management generally not considered editor user
  );

  const isClient = user?.user_type === "client";

  const [viewCustomerReport] = useViewCustomerReportMutation();
  const [sendCustomerReport] = useSendCustomerReportMutation();

  const onSendCustomerReport = async (installationId: any) => {
    try {
      await sendCustomerReport(installationId).unwrap();
      api.success({
        message: "Customer report send successfully",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.error({
        message: "Customer report send operation failed",
        placement: "top"
      });
    }
  };

  const onViewCustomerReport = async (installationId: any) => {
    try {
      const res = await viewCustomerReport(installationId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
      refetch();
    } catch (error: any) {
      api.error({
        message: "MSIL IR report operation failed",
        placement: "top"
      });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Scheduled Installation Date",
      dataIndex: "scheduled_install_date_time",
      key: "scheduled_install_date_time",
      render: (scheduled_install_date_time) => (
        <Text>
          {scheduled_install_date_time ? localizeDateTime(scheduled_install_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "Cable length",
      dataIndex: "cable_length_new",
      key: "cable_length_new"
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Installation Code",
      dataIndex: "ecb_install_code",
      key: "ecb_install_code",
      width: "150px",
      render: (ecb_install_code) => <>{ecb_install_code || "-"}</>
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => <>{assigned_city_manager?.name || "-"}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "MSIL IR Sending Status",
      dataIndex: "installation_report_sent_to_api",
      key: "installation_report_sent_to_api",
      render: (_, installation: any) => (
        <>
          {installation?.brand?.send_reports_to_api && installation?.status === "completed"
            ? installation?.installation_report_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "Partner",
      dataIndex: "install_partner",
      key: "install_partner",
      render: (install_partner) => <>{install_partner?.name || "-"}</>
    },
    {
      title: "Installation Status",
      dataIndex: "status",
      key: "status",
      render: (_, installation: any) => <>{getStatusText(installation)}</>
    },
    {
      title: "Customer Rating",
      dataIndex: "customer_rating",
      key: "customer_rating",
      render: (_, installation: any) => (
        <>
          {installation.customer_rating && installation?.status === "completed"
            ? `${installation.customer_rating}/5`
            : "-"}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, installation: any) => (
        <Space size="middle" direction="horizontal">
          <>
            <Link to={`${installation?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            {((installation?.status === "completed" && (isAdminUser || isMisUser)) ||
              installation?.status !== "completed") && (
              <Link to={`${installation?.id}/update`}>
                <Button icon={<EditOutlined />} title="Update"></Button>
              </Link>
            )}
            {installation?.status !== "cancelled" && (
              <>
                {(isAdminUser || isMisUser) && (
                  <Link to={`/${installation?.id}/print-installation`} target="_blank">
                    <Button icon={<PrinterOutlined />} title="Print"></Button>
                  </Link>
                )}
                <Link to={`/${installation?.id}/share-installation`} target="_blank">
                  <Button icon={<ShareAltOutlined />} title="Share"></Button>
                </Link>
                <Link to={`/${installation?.id}/customer-report-installation`} target="_blank">
                  <Button icon={<SendOutlined />} title="send"></Button>
                </Link>
              </>
            )}
            {installation?.status === "completed" && (
              <>
                {installation?.zip_file ? (
                  <Link to={installation?.zip_file} target="_blank">
                    <Button title="Download Zip">
                      <CloudDownloadOutlined />
                    </Button>
                  </Link>
                ) : (
                  <Button
                    disabled={
                      Object.prototype.hasOwnProperty.call(downloadInProgress, installation?.id) &&
                      downloadInProgress[installation?.id] === true
                    }
                    title="Download Zip"
                    onClick={() => handleDownload(installation?.id, installation?.ecb_install_code)}
                  >
                    {Object.prototype.hasOwnProperty.call(downloadInProgress, installation?.id) &&
                    downloadInProgress[installation?.id] === true ? (
                      <LoadingOutlined style={{ fontSize: 12 }} spin />
                    ) : (
                      <CloudDownloadOutlined />
                    )}
                  </Button>
                )}
              </>
            )}
            {installation?.brand?.send_reports_to_api && installation?.status === "completed" && (
              <>
                <Button
                  icon={<FilePdfOutlined />}
                  title="MSIL IR report PDF"
                  onClick={() => onViewCustomerReport(installation.id)}
                ></Button>
                <Button
                  icon={<CloudUploadOutlined />}
                  title="Send MSIL IR report"
                  onClick={() => onSendCustomerReport(installation.id)}
                ></Button>
              </>
            )}
          </>
        </Space>
      )
    }
  ];

  const clientColumns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Scheduled Installation Date",
      dataIndex: "scheduled_install_date_time",
      key: "scheduled_install_date_time",
      render: (scheduled_install_date_time) => (
        <Text>
          {scheduled_install_date_time ? localizeDateTime(scheduled_install_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Installation Code",
      dataIndex: "ecb_install_code",
      key: "ecb_install_code",
      width: "150px",
      render: (ecb_install_code) => <>{ecb_install_code || "-"}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "Installation Status",
      dataIndex: "status",
      key: "status",
      render: (_, installation: any) => <>{getStatusText(installation)}</>
    },
    {
      title: "Action",
      key: "action",
      render: (_, installation: any) => (
        <Space size="middle" direction="horizontal">
          <>
            <Link to={`${installation?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            {installation?.status === "completed" && (
              <Link to={`/${installation?.id}/share-installation`} target="_blank">
                <Button icon={<ShareAltOutlined />} title="Share"></Button>
              </Link>
            )}
          </>
        </Space>
      )
    }
  ];

  const financeColumns = [
    {
      title: "Base Amount",
      dataIndex: "base_amount",
      key: "base_amount"
    },
    {
      title: "GST Amount",
      dataIndex: "gst_amount",
      key: "gst_amount"
    },
    {
      title: "Invoice Total",
      dataIndex: "invoice_total",
      key: "invoice_total"
    },
    {
      title: "Amount to Receive",
      dataIndex: "amount_to_receive",
      key: "amount_to_receive"
    },
    {
      title: "Amount to Refund",
      dataIndex: "amount_to_refund",
      key: "amount_to_refund"
    }
  ];

  if (isSuperUser(user) && (tabKey == "2" || tabKey == "3")) {
    const lastIndex = 12; // 12 is index of column before action column
    columns.splice(lastIndex + 1, 0, ...financeColumns); // for insert columns at after an specified index
  }

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const onChange = (key: string) => {
    setTabKey(key);
    if (key === "1") {
      const params: any = queryParams;
      delete params.payment_received;
      setQueryParams(params);
    }
    if (key === "2") {
      const values: any = { payment_received: false };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "3") {
      const values: any = { payment_received: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const handleDownload = async (id: number, filename: string) => {
    try {
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: true
      }));
      const response = await fetch(`${BASE_URL}assignments/${id}/download_zip`, {
        method: "GET",
        headers: {
          "access-token": auth?.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        }
      });

      if (!response.ok) {
        setDownloadInProgress((prev: any) => ({
          ...prev,
          [id]: false
        }));
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();

      const downloadUrl = URL.createObjectURL(blob);

      // Create a link and click it to initiate download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${filename}.zip`;
      link.click();
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    }
  };

  const visibleReportIds = installations
    .filter((installation: any) => installation?.status === "completed")
    .map((installation: any) => installation?.id);

  const handleBulkReportDownload = async () => {
    try {
      setBulkReportLoading(true);
      const response: any = await bulkCustomerReports({
        resource: "installations",
        ids: visibleReportIds
      }).unwrap();
      api.success({
        message:
          response?.message ||
          "Request submitted. A download link will be emailed to you shortly.",
        placement: "top",
        duration: 8
      });
    } catch (error: any) {
      api.error({
        message: error?.data?.error || "Failed to prepare download. Please try again.",
        placement: "top"
      });
    } finally {
      setBulkReportLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Installations</h2>
            <Text>
              Total installations:
              <span style={{ paddingLeft: "4px" }}>{data?.meta?.total_count || 0}</span>
            </Text>
          </div>
          <div>
            <Space size="middle" wrap>
              {(isEditorUser || isAdminUser) && (
                <Tooltip
                  title={
                    installations.length > 50
                      ? "Not available for more than 50 records on the page"
                      : visibleReportIds.length === 0
                      ? "No completed installations on this page"
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="large"
                      type="primary"
                      icon={<CloudDownloadOutlined />}
                      loading={bulkReportLoading}
                      disabled={installations.length > 50 || visibleReportIds.length === 0}
                      onClick={handleBulkReportDownload}
                    >
                      Download {visibleReportIds.length} SR + IR Report(s)
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isEditorUser && (
                <Link to="upload-installation-invoice-claim">
                  <Button size="large" type="primary">
                    Upload Installation Invoices Claimed Date
                  </Button>
                </Link>
              )}
            </Space>
          </div>
        </Col>
      </Row>
      {isSuperUser(user) && <Tabs defaultActiveKey={tabKey} items={items} onChange={onChange} />}
      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        installations
      />
      <TopScrollTable
        rowKey="id"
        columns={isClient ? clientColumns : columns}
        dataSource={installations}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
    </>
  );
};

export default Installation;
